import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { Table } from 'reactstrap';
import { toast } from 'react-toastify';
import Loader from 'components/Loader';
import {
  fetchCollateral as fetchCollateralAction,
  fetchCollateralSummary as fetchCollateralSummaryAction,
} from 'actions/dashboard';
import {
  getCollateralSummaryHeldDisplay,
  getCollateralHistoryTransactions,
  mapCollateralHistoryRows,
} from '../utils/cryptoUtils';

const copyTransactionId = async (transactionHash) => {
  if (!transactionHash) {
    return;
  }

  try {
    await navigator.clipboard.writeText(transactionHash);
    toast.success('Transaction ID copied to clipboard');
  } catch {
    toast.error('Unable to copy transaction ID. Please copy it manually.');
  }
};

const CollateralHistoryPanel = ({
  loanId,
  coinType,
  collateral,
  collateralSummary,
  fetchCollateral,
  fetchCollateralSummary,
}) => {
  useEffect(() => {
    if (!loanId) {
      return;
    }

    fetchCollateral(loanId);
    fetchCollateralSummary(loanId);
  }, [loanId, fetchCollateral, fetchCollateralSummary]);

  const isLoading = collateral?.isLoading || collateralSummary?.isLoading;

  if (isLoading) {
    return <Loader className="height-2" />;
  }

  const summary = getCollateralSummaryHeldDisplay(collateralSummary?.data);
  const collateralHeld = summary.collateralHeld || '-';
  const rawTransactions = getCollateralHistoryTransactions(collateral?.data);
  const transactions = mapCollateralHistoryRows(rawTransactions, coinType);

  return (
    <div className="collateral-history-panel">
      <div className="collateral-history-panel__summary">
        <div className="collateral-history-panel__summary-metrics">
          <div className="collateral-history-panel__metric">
            <small className="collateral-history-panel__metric-label">Collateral Held</small>
            <strong className="collateral-history-panel__metric-value collateral-history-panel__metric-value--held">
              {collateralHeld}
            </strong>
          </div>
        </div>
      </div>

      <Table className="collateral-history-panel__table mb-0" size="sm" responsive striped borderless>
        <thead>
          <tr>
            <th>Date</th>
            <th>Transfer</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Transaction ID</th>
          </tr>
        </thead>
        <tbody>
          {transactions.length > 0 ? transactions.map((item, index) => (
            <tr key={`${item.date}-${item.transferType}-${index}`}>
              <th scope="row">{moment(new Date(item.date)).format('MM/DD/YYYY').toString()}</th>
              <td>{item.typeLabel}</td>
              <td className="collateral-history-panel__amount">{item.amountDisplay || '-'}</td>
              <td className="collateral-history-panel__status">{item.statusLabel || '-'}</td>
              <td>
                {item.transactionHash ? (
                  <div className="collateral-history-panel__transaction-id">
                    <span className="collateral-history-panel__transaction-id-text">
                      {item.transactionIdDisplay}
                    </span>
                    <button
                      type="button"
                      className="collateral-history-panel__copy-btn"
                      title="Copy transaction ID"
                      aria-label="Copy transaction ID"
                      onClick={() => copyTransactionId(item.transactionHash)}
                    >
                      <span className="collateral-history-panel__copy-icon" aria-hidden="true">⧉</span>
                    </button>
                  </div>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan={5} align="center" className="pt-3 pb-3">No data</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

CollateralHistoryPanel.propTypes = {
  loanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  coinType: PropTypes.string,
  collateral: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    isLoading: PropTypes.bool,
  }),
  collateralSummary: PropTypes.shape({
    data: PropTypes.object,
    isLoading: PropTypes.bool,
  }),
  fetchCollateral: PropTypes.func.isRequired,
  fetchCollateralSummary: PropTypes.func.isRequired,
};

CollateralHistoryPanel.defaultProps = {
  coinType: 'BTC',
  collateral: {},
  collateralSummary: {},
};

const mapStateToProps = (state) => ({
  collateral: state.dashboard.collateral,
  collateralSummary: state.dashboard.collateralSummary,
});

export default compose(
  connect(mapStateToProps, {
    fetchCollateral: fetchCollateralAction,
    fetchCollateralSummary: fetchCollateralSummaryAction,
  })
)(CollateralHistoryPanel);
